import React, { useState, useMemo } from 'react';
import { CmAugmentation, CmEntity } from '../types';
import { ChevronDown, ChevronUp, Copy, Info } from 'lucide-react';

interface MedicalComprehendComponentProps {
  cmAugmentation: CmAugmentation;
  transcript?: string;
}

interface ProcessedEntity extends CmEntity {
  mergedTraits: string[];
  isNegated: boolean;
  isHypothetical: boolean;
  isPastHistory: boolean;
  confidenceLevel: 'high' | 'medium' | 'low';
}

interface GroupedEntities {
  symptoms: ProcessedEntity[];
  medications: ProcessedEntity[];
  conditions: ProcessedEntity[];
  procedures: ProcessedEntity[];
  social: ProcessedEntity[];
  anatomy: ProcessedEntity[];
  other: ProcessedEntity[];
}

export const MedicalComprehendComponent: React.FC<MedicalComprehendComponentProps> = ({
  cmAugmentation
}) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    'symptoms': true,
    'conditions': true,
    'medications': true,
    'procedures': false,
    'social': false,
    'anatomy': false,
    'other': false
  });
  
  const [copiedStates, setCopiedStates] = useState<Record<string, boolean>>({});

  // Process entities with deduplication and trait merging
  const processedEntities = useMemo(() => {
    if (!cmAugmentation.entities) return [];

    const entityMap = new Map<string, ProcessedEntity>();
    
    cmAugmentation.entities.forEach(entity => {
      if (!entity.text || !entity.score) return;
      
      const key = entity.text.toLowerCase().trim();
      const existing = entityMap.get(key);
      
      if (!existing || (entity.score > (existing.score || 0))) {
        const mergedTraits = [
          ...(entity.traits || []).map(t => t.name || ''),
          ...(entity.evidence || []).map(e => e.name || '')
        ].filter(Boolean);
        
        const isNegated = mergedTraits.includes('NEGATION');
        const isHypothetical = mergedTraits.includes('HYPOTHETICAL');
        const isPastHistory = mergedTraits.includes('PAST_HISTORY');
        
        let confidenceLevel: 'high' | 'medium' | 'low' = 'low';
        if (entity.score >= 0.80) confidenceLevel = 'high';
        else if (entity.score >= 0.60) confidenceLevel = 'medium';
        
        entityMap.set(key, {
          ...entity,
          mergedTraits,
          isNegated,
          isHypothetical,
          isPastHistory,
          confidenceLevel
        });
      }
    });
    
    return Array.from(entityMap.values());
  }, [cmAugmentation.entities]);

  // Group entities by category
  const groupedEntities = useMemo((): GroupedEntities => {
    const groups: GroupedEntities = {
      symptoms: [],
      medications: [],
      conditions: [],
      procedures: [],
      social: [],
      anatomy: [],
      other: []
    };

    processedEntities.forEach(entity => {
      const category = entity.category?.toLowerCase() || '';
      const type = entity.type?.toLowerCase() || '';
      
      if (category.includes('medical_condition') || type.includes('dx_name')) {
        groups.conditions.push(entity);
      } else if (category.includes('medication') || type.includes('generic_name')) {
        groups.medications.push(entity);
      } else if (category.includes('test_treatment_procedure') || type.includes('procedure_name') || type.includes('test_name')) {
        groups.procedures.push(entity);
      } else if (category.includes('behavioral_environmental_social') || type.includes('tobacco_use') || type.includes('alcohol_consumption') || type.includes('rec_drug_use')) {
        groups.social.push(entity);
      } else if (category.includes('anatomy') || type.includes('system_organ_site')) {
        groups.anatomy.push(entity);
      } else if (entity.mergedTraits.includes('SYMPTOM')) {
        groups.symptoms.push(entity);
      } else {
        groups.other.push(entity);
      }
    });

    return groups;
  }, [processedEntities]);

  const toggleSection = (sectionKey: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };


  const copyToClipboard = async (text: string, buttonKey: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStates(prev => ({ ...prev, [buttonKey]: true }));
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [buttonKey]: false }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  // Removed confidence/category helpers to avoid unused code

  const renderEntityCard = (entity: ProcessedEntity) => {
    const entityId = `entity-${entity.id}`;
    const isCopied = copiedStates[entityId];

    // Get related codes
    const relatedCodes = {
      icd10: cmAugmentation.codes?.icd10?.filter(code => 
        code.text?.toLowerCase().includes(entity.text?.toLowerCase() || '')
      ) || [],
      rxnorm: cmAugmentation.codes?.rxnorm?.filter(code => 
        code.text?.toLowerCase().includes(entity.text?.toLowerCase() || '')
      ) || [],
      snomed: cmAugmentation.codes?.snomed?.filter(code => 
        code.text?.toLowerCase().includes(entity.text?.toLowerCase() || '')
      ) || []
    };

    return (
      <div key={entity.id} className="card bg-base-100 border border-base-300 shadow-sm">
        <div className="card-body p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-medium text-base-content">
                  {entity.isNegated ? 'No ' : ''}{entity.text}
                </h4>
                {entity.isNegated && <div className="badge badge-error badge-sm">Negated</div>}
                {entity.isHypothetical && <div className="badge badge-warning badge-sm">Possible</div>}
                {entity.isPastHistory && <div className="badge badge-info badge-sm">Past</div>}
              </div>

              <div className="text-sm text-base-content/70">
                {entity.category?.replace(/_/g, ' ')}
              </div>

              {(relatedCodes.icd10.length > 0 || relatedCodes.rxnorm.length > 0 || relatedCodes.snomed.length > 0) && (
                <div className="mt-2 text-sm text-base-content/70 flex flex-wrap gap-2">
                  {relatedCodes.icd10.length > 0 && (
                    <div>
                      <span className="font-medium">ICD-10:</span> {relatedCodes.icd10.map((c) => `${c.code}`).join(', ')}
                    </div>
                  )}
                  {relatedCodes.rxnorm.length > 0 && (
                    <div>
                      <span className="font-medium">RxNorm:</span> {relatedCodes.rxnorm.map((c) => `${c.text}`).join(', ')}
                    </div>
                  )}
                  {relatedCodes.snomed.length > 0 && (
                    <div>
                      <span className="font-medium">SNOMED:</span> {relatedCodes.snomed.map((c) => `${c.code}`).join(', ')}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center gap-1 ml-2">
              <button
                className="btn btn-ghost btn-xs"
                onClick={() => copyToClipboard(entity.text || '', entityId)}
                title="Copy to clipboard"
              >
                <Copy className="w-3 h-3" />
                {isCopied && <span className="text-xs">Copied!</span>}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderEntityGroup = (title: string, entities: ProcessedEntity[], groupKey: string) => {
    if (entities.length === 0) return null;

    const isExpanded = expandedSections[groupKey];

    return (
      <div className="border border-base-300 rounded-lg">
        <button
          className="w-full flex items-center justify-between p-4 text-left hover:bg-base-200 transition-colors duration-200 focus:outline-none"
          onClick={() => toggleSection(groupKey)}
        >
          <div className="flex items-center space-x-3">
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-base-content/50" />
            ) : (
              <ChevronUp className="w-5 h-5 text-base-content/50" />
            )}
            <span className="font-medium text-base-content">
              {title} ({entities.length})
            </span>
          </div>
        </button>
        
        {isExpanded && (
          <div className="px-4 pb-4">
            <div className="space-y-3">
              {entities.map(renderEntityCard)}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!cmAugmentation.entities || cmAugmentation.entities.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="mx-auto flex items-center justify-center h-12 w-12 bg-blue-100 rounded-full mb-4">
          <Info className="h-6 w-6 text-blue-600" />
        </div>
        <h3 className="text-lg font-semibold text-base-content mb-2">No Medical Entities Detected</h3>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-base-content">Clinical Findings from Transcript</h3>
        {renderEntityGroup('Symptoms & Findings', groupedEntities.symptoms, 'symptoms')}
        {renderEntityGroup('Diagnoses & Conditions', groupedEntities.conditions, 'conditions')}
        {renderEntityGroup('Medications', groupedEntities.medications, 'medications')}
        {renderEntityGroup('Procedures & Tests', groupedEntities.procedures, 'procedures')}
        {renderEntityGroup('Social History', groupedEntities.social, 'social')}
        {renderEntityGroup('Anatomy', groupedEntities.anatomy, 'anatomy')}
        {renderEntityGroup('Other', groupedEntities.other, 'other')}
      </div>
    </div>
  );
};
